import { SicFlyout } from './sic-flyout/sic-flyout';
import { SicNavItem } from './sic-nav-item/sic-nav-item';
import { SicNavLink } from './sic-nav-link/sic-nav-link';
import { SicSearch } from '../sic-search/sic-search';
import { MainNavData, MenuNavItem, JsonAPIResult } from './types';

/**
 * Takes fetch result, errors if none, and processes data into MenuNavItem objects.
 * @param fetchedData
 * @returns Array<MenuNavItem>
 */
export const processMenuData = (fetchedData: JsonAPIResult) => {
  const data = fetchedData.data.length > 0 ? fetchedData.data : false;
  const links: MainNavData = [];

  if (!data) {
    throw new Error('Menu data array empty');
  }

  if (data) {
    data.forEach(entry => {
      //set blank
      const link: MenuNavItem = {
        text: '',
        id: '',
        url: undefined,
        aria: undefined,
      };

      const recursiveParentFetch = (
        subFolder: MenuNavItem[],
        parentID: string,
        child: MenuNavItem
      ): void => {
        return subFolder.forEach(entry => {
          if (entry.id === parentID) {
            handleSubFolder(entry, child);
          }
          if (entry.sub) {
            return recursiveParentFetch(entry.sub, parentID, child);
          }
        });
      };

      const handleSubFolder = (parent: MenuNavItem, child: MenuNavItem) => {
        if (parent.sub) {
          parent.sub.push(child);
        } else {
          parent.sub = [];
          parent.sub.push(child);
        }
      };

      // verify link is valid
      if (entry.attributes.title && entry.id) {
        link.text = entry.attributes.title;
        link.id = entry.id.split(':')[1];

        if (entry.attributes.url) {
          link.url = entry.attributes.url;
        }
        if (
          entry.attributes.options.attributes &&
          entry.attributes.options.attributes['aria-label']
        ) {
          link.aria = entry.attributes.options.attributes['aria-label'];
        }
        // if parent, push to parent.sub
        if (entry.attributes.parent) {
          // Can't have a colon in an id.
          const parentID = entry.attributes.parent.split(':')[1];
          link.parentID = parentID;
          recursiveParentFetch(links, parentID, link);
        } else {
          links.push(link);
        }
      }
    });
  }
  return links;
};

/**
 * Removes 'open' attribute from menu sub-components.
 */
export const removeHangingOpen = () => {
  const hangingOpen = document
    .querySelector('sic-nav')
    ?.shadowRoot?.querySelector('[open]');

  if (hangingOpen) {
    hangingOpen.removeAttribute('open');
  }
};

/**
 * @param url
 * @returns Window path.
 */
export const getCurrentPath = (url: string) => {
  return window.location.pathname === url && url !== '/';
};

/**
 * @param vars: Array of css vars, for use with with number values like 12px or 3rem;
 * @returns Number: sum total of all var values.
 */
export const getVarNumberValue = (vars: Array<string>) => {
  const values = vars.map(variable => {
    const value = getComputedStyle(document.documentElement).getPropertyValue(
      variable
    );
    return value === '' ? 0 : Number(value.replace(/[^0-9]/g, ''));
  });
  return values.reduce((a, b) => a + b, 0);
};

export const _dispatchMenuOpenEvent = (
  self: SicNavItem | SicFlyout | SicNavLink | SicSearch
) => {
  const options = {
    detail: 'click',
    bubbles: true,
    composed: true,
  };
  self.dispatchEvent(new CustomEvent('menuOpen', options));
};

/**
 * Custom event to dispatch parent element ID when the component is part of the active trail.
 */
export const _dispatchActiveEvent = (
  self: SicNavItem | SicFlyout | SicNavLink
) => {
  const parentID = self?.item?.parentID;
  if (parentID) {
    const options = {
      detail: { parentID },
      bubbles: true,
      composed: true,
    };
    self.dispatchEvent(new CustomEvent('activeListener', options));
  }
};
